import React, { useState, useEffect, useRef } from 'react';
import { View, Text, TextInput, StyleSheet, TouchableOpacity, FlatList, ActivityIndicator, KeyboardAvoidingView, Platform, Linking, Switch, Image } from 'react-native';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, db } from '../firebase.config';
import { doc, getDoc, setDoc, collection, query, getDocs } from 'firebase/firestore';
import { formatPhoneNumberDashes as formatPhoneNumber, cleanPhoneNumber } from "../utils/formatting";
import { getCollectionPath } from '../utils/dbPaths';
import { getTenantStoreContext } from '../services/userStoreCache';
import { subscribeToConversationMessages, unsubscribeFromConversationMessages } from '../services/messagesListener';
import { useMessagesStore, useCustomer, getSelectedUserId } from '../stores';

const auth = getAuth(app);
const functions = getFunctions(app);

const Messages = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const flatListRef = useRef(null);
  
  // Get customer info from navigation state
  const stateCustomer = location.state?.customer;

  // Tapping a push notification opens /messages?phone=1234567890. That is a
  // cold navigation -- it carries no React Router state -- so when state is
  // absent, resolve the customer from the URL instead.
  const phoneParam = new URLSearchParams(location.search).get('phone');
  const [linkedCustomer, setLinkedCustomer] = useState(null);
  const customer = stateCustomer || linkedCustomer;

  // getTenantStoreContext() is a plain cache read, not reactive, and auth
  // resolves before the user's tenant/store doc finishes loading. That race
  // used to be rare; opening the app from a notification makes a cold start
  // the normal path, so wait for the context and re-render when it lands --
  // otherwise every path below builds against the wrong tenant.
  const [contextTick, setContextTick] = useState(0);

  useEffect(() => {
    const ready = getTenantStoreContext();
    if (ready.tenantId && ready.storeId) return;

    const interval = setInterval(() => {
      const ctx = getTenantStoreContext();
      if (ctx.tenantId && ctx.storeId) {
        clearInterval(interval);
        setContextTick((tick) => tick + 1);
      }
    }, 200);
    const timeout = setTimeout(() => clearInterval(interval), 15000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [contextTick]);

  useEffect(() => {
    if (stateCustomer || !phoneParam) return;

    const ctx = getTenantStoreContext();
    if (!ctx.tenantId || !ctx.storeId) return; // retries when contextTick moves

    let cancelled = false;

    (async () => {
      const cleanedPhone = cleanPhoneNumber(phoneParam);
      // Fall back to the number itself so the header is never blank if the
      // conversation doc is missing or offline.
      let fullName = formatPhoneNumber(cleanedPhone);

      try {
        const conversationRef = doc(
          db,
          getCollectionPath('conversations'),
          cleanedPhone
        );
        const snapshot = await getDoc(conversationRef);
        if (snapshot.exists() && snapshot.data().customerName) {
          fullName = snapshot.data().customerName;
        }
      } catch (error) {
        console.warn('⚠️ Could not load conversation for deep link:', error);
      }

      if (!cancelled) {
        setLinkedCustomer({ id: cleanedPhone, phone: cleanedPhone, fullName });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [stateCustomer, phoneParam, contextTick]);

  // Get Zustand store functions
  const setCustomer = useCustomer((state) => state.setCustomer);
  
  // Get messages from Zustand store
  const messages = useMessagesStore(state => 
    customer?.phone ? state.getMessages(cleanPhoneNumber(customer.phone)) : []
  );
          
          const [messageText, setMessageText] = useState('');
          const [sending, setSending] = useState(false);
          const [loading, setLoading] = useState(false);
          const [inputHeight, setInputHeight] = useState(40);
          const [isMobile, setIsMobile] = useState(Platform.OS !== 'web' || (typeof window !== 'undefined' && window.innerWidth < 768));
          
          // New state for canRespond
          const [canRespond, setCanRespond] = useState(false);
          const [loadingCanRespond, setLoadingCanRespond] = useState(true);

          // Video modal state
          const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
          const [videoModalUrl, setVideoModalUrl] = useState(null);
          const [isVideoPlaying, setIsVideoPlaying] = useState(false);
          const videoPlayerRef = useRef(null);

          // Photo modal state
          const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
          const [photoModalUrl, setPhotoModalUrl] = useState(null);

          // Camera state
          const [sendingPhoto, setSendingPhoto] = useState(false);
          const [cameraLoading, setCameraLoading] = useState(false);

          // Get tenant/store context
          const { tenantId, storeId } = getTenantStoreContext();
          const [showAutomatedOnly, setShowAutomatedOnly] = useState(false);

          const automatedMessages = messages.filter(
            (message) => message.messageType === 'automated_booking_sms'
          );
          const displayedMessages = showAutomatedOnly ? automatedMessages : messages;

          // Scroll to bottom when messages change
  useEffect(() => {
    if (flatListRef.current && displayedMessages.length > 0) {
      // Use setTimeout to ensure FlatList is fully rendered
      setTimeout(() => {
        if (flatListRef.current) {
          flatListRef.current.scrollToEnd({ animated: true });
        }
      }, 100);
    }
  }, [displayedMessages]);

  // Scroll to bottom on initial page load
  useEffect(() => {
    if (flatListRef.current && displayedMessages.length > 0 && !loading) {
      // Delay to ensure FlatList is fully rendered and laid out
      const scrollTimeout = setTimeout(() => {
        if (flatListRef.current) {
          flatListRef.current.scrollToEnd({ animated: false });
        }
      }, 300);
      
      return () => clearTimeout(scrollTimeout);
    }
  }, [customer?.phone, loading, displayedMessages.length]); // Trigger when customer changes or loading completes

  useEffect(() => {
    if (Platform.OS === 'web') {
      const handleResize = () => setIsMobile(window.innerWidth < 768);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);
  
  useEffect(() => {
    if (!customer || !customer.phone) {
      // Deep link still resolving -- don't bounce the user out from under it.
      if (phoneParam) return;
      alert('No customer selected');
      navigate(-1);
      return;
    }

    console.log('📱 Messages component mounted for customer:', customer.fullName);
    
    // Load canRespond status
    loadCanRespondStatus();

    // Mark conversation as read when opening
    markAsRead();
    
    // Load messages directly if not in store (fallback)
    loadMessagesDirectly();

  }, [customer, navigate, phoneParam]);

  // Subscribe to this conversation's messages in real time (one listener per open thread)
  useEffect(() => {
    if (!customer?.phone || !tenantId || !storeId) return;
    const cleanedPhone = cleanPhoneNumber(customer.phone);
    subscribeToConversationMessages(tenantId, storeId, cleanedPhone);
    return () => unsubscribeFromConversationMessages(cleanedPhone);
  }, [customer?.phone, tenantId, storeId]);

  // Fallback: Load messages directly from Firestore if not in store
  const loadMessagesDirectly = async () => {
    if (!customer?.phone || !tenantId || !storeId) return;
    
    const cleanedPhone = cleanPhoneNumber(customer.phone);
    const storeMessages = useMessagesStore.getState().getMessages(cleanedPhone);
    
    // Only load if we don't have messages in the store
    if (storeMessages.length === 0) {
      console.log('📥 Loading messages directly from Firestore for:', cleanedPhone);
      try {
        const messagesRef = collection(db, 'tenants', tenantId, 'stores', storeId, 'conversations', cleanedPhone, 'messages');
        const messagesSnapshot = await getDocs(messagesRef);
        
        const messages = [];
        messagesSnapshot.forEach((doc) => {
          messages.push({ id: doc.id, ...doc.data() });
        });
        
        // Sort messages by timestamp
        messages.sort((a, b) => {
          const getTimestamp = (msg) => {
            const ts = msg.sentAt || msg.receivedAt || msg.createdAt;
            if (!ts) return 0;
            if (ts.seconds) return ts.seconds * 1000;
            if (ts instanceof Date) return ts.getTime();
            return ts;
          };
          return getTimestamp(a) - getTimestamp(b);
        });
        
        if (messages.length > 0) {
          console.log(`✅ Loaded ${messages.length} messages directly from Firestore`);
          useMessagesStore.getState().setConversation(cleanedPhone, {
            messages: messages
          });
        }
      } catch (error) {
        console.error('❌ Error loading messages directly:', error);
      }
    }
  };
  
  const loadCanRespondStatus = async () => {
    if (!customer?.phone) return;
    
    try {
      const conversationsPath = getCollectionPath('conversations');
      const infoDocRef = doc(db, conversationsPath, customer.phone, 'info', 'settings'); 
      // User said "conversations/{phoneNumber}/info object" - typically means "conversations/{phoneNumber}/info" document if it's a doc ID "info"
      // BUT paths must alternate. conversations(col) -> phone(doc) -> info(subcol) -> settings(doc)?
      // OR conversations(col) -> phone(doc) -> info(map field)?
      // Let's try to read the document `conversations/{phoneNumber}/info` directly assuming `info` is a document in a subcollection? No.
      // Assuming `conversations/{phoneNumber}` document has an `info` field:
      // const docRef = doc(db, conversationsPath, customer.phone);
      // const docSnap = await getDoc(docRef);
      // if (docSnap.exists() && docSnap.data().info) setCanRespond(docSnap.data().info.canRespond);
      
      // But user said "conversations/{phoneNumber}/info object". This usually refers to a specific document path if using the term "object" in a NoSQL context often implies a Document.
      // However, typical Firestore structure `col/doc/subcol/doc`.
      // Let's assume the user meant a document at `conversations/{phoneNumber}/info` which is invalid (odd number of segments).
      // Maybe `conversations` is a subcollection of something? `getCollectionPath` handles that.
      // If `conversations` path is `tenants/T/stores/S/conversations`, then `.../conversations/{phone}/info` is valid if `info` is a subcollection? No, still need a doc ID.
      
      // Let's assume the user means `conversations/{phoneNumber}` document, and we update a field.
      // BUT "new boolean field in the conversations/{phoneNumber}/info object" specific phrasing.
      // It might mean: `conversations/{phoneNumber}` doc has a field named `info` which is a map, and we add `canRespond` to that map.
      // Let's go with that as it's the most standard interpretation of "field in an object" within a document.
      
      // WAIT, if "conversations/{phoneNumber}/info" IS a document, then `conversations/{phoneNumber}` must be a collection?
      // No, `subscribeToMessages` uses `${conversationsPath}/${customerPhone}/messages`.
      
      // Let's stick to `conversations/{phoneNumber}` document, field `info` (map), key `canRespond`.
      
      const docRef = doc(db, conversationsPath, customer.phone);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.info && typeof data.info.canRespond !== 'undefined') {
          setCanRespond(data.info.canRespond);
        } else {
          // Default to false or maybe true? User didn't specify default.
          // If "Always open day of pickup/delivery", maybe default false unless manually enabled?
          setCanRespond(false);
        }
      }
      setLoadingCanRespond(false);
    } catch (error) {
      console.error("Error loading canRespond status:", error);
      setLoadingCanRespond(false);
    }
  };

  const handleCanRespondToggle = async (newValue) => {
    setCanRespond(newValue); // Optimistic update
    
    try {
      // Get multi-tenant conversation path
      const conversationsPath = getCollectionPath('conversations');
      const docRef = doc(db, conversationsPath, customer.phone);
      
      // Update the info.canRespond field
      await setDoc(docRef, {
        info: {
          canRespond: newValue
        }
      }, { merge: true });
      
      console.log(`✅ Updated canRespond to ${newValue}`);
    } catch (error) {
      const errorDetails = {
        errorType: error.constructor?.name || 'Unknown',
        errorMessage: error.message || 'No error message',
        errorCode: error.code || 'No error code',
        errorStack: error.stack || 'No stack trace',
        requestDetails: {
          customerPhone: customer.phone,
          newValue: newValue,
          tenantId: tenantId,
          storeId: storeId,
        },
        timestamp: new Date().toISOString(),
      };
      console.error("❌ Failed to update canRespond:", errorDetails);
      console.error("❌ Full error:", error);
      setCanRespond(!newValue); // Revert on error
      alert("Failed to update status: " + (error.message || 'Unknown error'));
    }
  };

  const markAsRead = async () => {
    try {
      console.log('📖 Marking conversation as read:', {
        customerPhone: customer.phone,
        tenantId: tenantId,
        storeId: storeId,
        timestamp: new Date().toISOString(),
      });

      const markConversationRead = httpsCallable(functions, 'markConversationRead');
      await markConversationRead({ 
        customerPhone: customer.phone,
        tenantId: tenantId,
        storeId: storeId
      });
      console.log('✅ Conversation marked as read');
    } catch (error) {
      const errorDetails = {
        errorType: error.constructor?.name || 'Unknown',
        errorMessage: error.message || 'No error message',
        errorCode: error.code || 'No error code',
        errorStack: error.stack || 'No stack trace',
        requestDetails: {
          customerPhone: customer.phone,
          tenantId: tenantId,
          storeId: storeId,
        },
        timestamp: new Date().toISOString(),
      };
      console.error('❌ Failed to mark conversation as read:', errorDetails);
      console.error('❌ Full error:', error);
    }
  };
  
          const handleSendMessage = async () => {
            if (!messageText.trim()) {
              return;
            }
            
            setSending(true);
            const text = messageText.trim();

            // 0) Optimistic update to Zustand store
            const tempId = `temp_${Date.now()}`;
            const optimisticMessage = {
              id: tempId,
              text: text,
              direction: 'outbound',
              sentAt: { seconds: Date.now() / 1000 },
              status: 'sending'
            };
            
            // Add to store immediately
            useMessagesStore.getState().setConversation(customer.phone, {
              messages: [...messages, optimisticMessage]
            });

            try {
              console.log('📤 Attempting to send SMS:', {
                to: customer.phone,
                messageLength: text.length,
                messagePreview: text.substring(0, 50) + (text.length > 50 ? '...' : ''),
                customerName: customer.fullName,
                tenantId: tenantId,
                storeId: storeId,
                timestamp: new Date().toISOString(),
              });

              const sendSMS = httpsCallable(functions, 'sendSMS');
              const result = await sendSMS({
                to: customer.phone,
                message: text,
                customerName: customer.fullName,
                customerPhone: customer.phone,
                tenantId: tenantId,
                storeId: storeId,
                // Rides along on the saved message so this customer's reply
                // gets forwarded to whoever is at the keyboard right now.
                sentByUserId: getSelectedUserId()
              });
              
              console.log('✅ SMS sent successfully:', {
                result: result.data,
                messageId: result.data?.messageSid,
                status: result.data?.status,
                timestamp: new Date().toISOString(),
              });
              
              setMessageText('');

              // sendSMS just turned canRespond on server-side. This state was
              // read once with getDoc on mount, so without this the switch keeps
              // showing "off" until the thread is reopened -- which reads as the
              // send having failed to enable replies.
              setCanRespond(true);
              
              setTimeout(() => {
                if (flatListRef.current) {
                  flatListRef.current.scrollToEnd({ animated: true });
                }
              }, 100);
              
            } catch (error) {
              // Comprehensive error logging
              const errorDetails = {
                errorType: error.constructor?.name || 'Unknown',
                errorMessage: error.message || 'No error message',
                errorCode: error.code || 'No error code',
                errorDetails: error.details || 'No error details',
                errorStack: error.stack || 'No stack trace',
                requestDetails: {
                  to: customer.phone,
                  messageLength: text.length,
                  messagePreview: text.substring(0, 50) + (text.length > 50 ? '...' : ''),
                  customerName: customer.fullName,
                  tenantId: tenantId,
                  storeId: storeId,
                },
                fullError: error,
                timestamp: new Date().toISOString(),
              };

              console.error('❌ Failed to send SMS - Full error details:', errorDetails);
              console.error('❌ Error object:', error);
              console.error('❌ Error message:', error.message);
              console.error('❌ Error code:', error.code);
              console.error('❌ Error details:', error.details);
              
              // Log Firebase-specific error properties
              if (error.code) {
                console.error('❌ Firebase error code:', error.code);
              }
              if (error.details) {
                console.error('❌ Firebase error details:', error.details);
              }
              if (error.message) {
                console.error('❌ Error message:', error.message);
              }

              // Try to extract more information from the error
              try {
                const errorString = JSON.stringify(error, Object.getOwnPropertyNames(error), 2);
                console.error('❌ Full error JSON:', errorString);
              } catch (jsonError) {
                console.error('❌ Could not stringify error:', jsonError);
              }

              // Remove optimistic message from store on error
              try {
                const currentMessages = useMessagesStore.getState().getMessages(cleanPhoneNumber(customer.phone));
                const filteredMessages = currentMessages.filter(msg => msg.id !== tempId);
                useMessagesStore.getState().setConversation(customer.phone, {
                  messages: filteredMessages
                });
                console.log('🗑️ Removed optimistic message from store');
              } catch (storeError) {
                console.error('❌ Failed to remove optimistic message:', storeError);
              }

              // Show user-friendly error message
              let userMessage = 'Failed to send message. ';
              if (error.message) {
                userMessage += error.message;
              } else if (error.code) {
                userMessage += `Error code: ${error.code}`;
              } else {
                userMessage += 'Unknown error occurred. Please check console for details.';
              }
              
              alert(userMessage);
            } finally {
              setSending(false);
            }
          };
  
  const handlePhonePress = () => {
    if (isMobile && customer.phone) {
      Linking.openURL(`tel:${customer.phone}`);
    }
  };

  // Capture photo using same method as ViewBooking page
  const capturePhoto = async () => {
    // Check if getUserMedia is available
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error("Camera access not supported by your browser");
    }

    console.log("📸 Requesting camera access...");
    setCameraLoading(true);

    // Request camera access
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: "environment", // Prefer back camera on mobile
        width: { ideal: 1920 },
        height: { ideal: 1080 }
      }
    });

    // Create video element to display camera stream
    const video = document.createElement("video");
    video.srcObject = stream;
    video.autoplay = true;
    video.playsInline = true;
    video.style.position = "fixed";
    video.style.top = "0";
    video.style.left = "0";
    video.style.width = "100%";
    video.style.height = "100%";
    video.style.objectFit = "cover";
    video.style.zIndex = "10000";
    video.style.backgroundColor = "#0F1A19";

    // Create send button (green checkmark)
    const sendButton = document.createElement("button");
    sendButton.innerHTML = "✓";
    sendButton.style.position = "fixed";
    sendButton.style.bottom = "40px";
    sendButton.style.right = "20px";
    sendButton.style.zIndex = "10001";
    sendButton.style.width = "60px";
    sendButton.style.height = "60px";
    sendButton.style.fontSize = "32px";
    sendButton.style.fontWeight = "bold";
    sendButton.style.backgroundColor = "#1F8A5B";
    sendButton.style.color = "#FFFFFF";
    sendButton.style.border = "none";
    sendButton.style.borderRadius = "30px";
    sendButton.style.cursor = "pointer";
    sendButton.style.boxShadow = "0 4px 12px rgba(0,0,0,0.3)";
    sendButton.style.display = "none"; // Hidden until photo is captured

    // Create retake button (icon-only, positioned to the left of send button)
    const retakeButton = document.createElement("button");
    retakeButton.innerHTML = "🔄";
    retakeButton.style.position = "fixed";
    retakeButton.style.bottom = "40px";
    retakeButton.style.right = "100px"; // Position to the left of send button
    retakeButton.style.zIndex = "10001";
    retakeButton.style.width = "60px";
    retakeButton.style.height = "60px";
    retakeButton.style.fontSize = "28px";
    retakeButton.style.backgroundColor = "rgba(255,255,255,0.2)";
    retakeButton.style.color = "#FFFFFF";
    retakeButton.style.border = "2px solid #FFFFFF";
    retakeButton.style.borderRadius = "30px";
    retakeButton.style.cursor = "pointer";
    retakeButton.style.boxShadow = "0 4px 12px rgba(0,0,0,0.3)";
    retakeButton.style.display = "none"; // Hidden until photo is captured

    // Create capture button
    const captureButton = document.createElement("button");
    captureButton.innerHTML = "📸";
    captureButton.style.position = "fixed";
    captureButton.style.bottom = "40px";
    captureButton.style.left = "50%";
    captureButton.style.transform = "translateX(-50%)";
    captureButton.style.zIndex = "10001";
    captureButton.style.width = "70px";
    captureButton.style.height = "70px";
    captureButton.style.fontSize = "36px";
    captureButton.style.backgroundColor = "rgba(255,255,255,0.3)";
    captureButton.style.color = "#FFFFFF";
    captureButton.style.border = "4px solid #FFFFFF";
    captureButton.style.borderRadius = "35px";
    captureButton.style.cursor = "pointer";
    captureButton.style.boxShadow = "0 4px 12px rgba(0,0,0,0.3)";

    // Create close button
    const closeButton = document.createElement("button");
    closeButton.innerHTML = "✕";
    closeButton.style.position = "fixed";
    closeButton.style.top = "20px";
    closeButton.style.right = "20px";
    closeButton.style.zIndex = "10001";
    closeButton.style.width = "50px";
    closeButton.style.height = "50px";
    closeButton.style.fontSize = "24px";
    closeButton.style.fontWeight = "bold";
    closeButton.style.backgroundColor = "rgba(0,0,0,0.7)";
    closeButton.style.color = "#FFFFFF";
    closeButton.style.border = "none";
    closeButton.style.borderRadius = "25px";
    closeButton.style.cursor = "pointer";

    // Create preview image element (hidden initially)
    const previewImage = document.createElement("img");
    previewImage.style.position = "fixed";
    previewImage.style.top = "0";
    previewImage.style.left = "0";
    previewImage.style.width = "100%";
    previewImage.style.height = "100%";
    previewImage.style.objectFit = "contain";
    previewImage.style.zIndex = "10000";
    previewImage.style.backgroundColor = "#0F1A19";
    previewImage.style.display = "none";

    document.body.appendChild(video);
    document.body.appendChild(captureButton);
    document.body.appendChild(sendButton);
    document.body.appendChild(retakeButton);
    document.body.appendChild(closeButton);
    document.body.appendChild(previewImage);

    // Wait for video to be ready
    await new Promise((resolve) => {
      video.onloadedmetadata = () => {
        video.play();
        setCameraLoading(false); // Hide loading modal when camera is ready
        resolve();
      };
    });

    // Capture photo when button is clicked
    const imageBase64 = await new Promise((resolve, reject) => {
      let capturedBase64 = null;

      const cleanup = () => {
        stream.getTracks().forEach(track => track.stop());
        document.body.removeChild(video);
        document.body.removeChild(captureButton);
        document.body.removeChild(sendButton);
        document.body.removeChild(retakeButton);
        document.body.removeChild(closeButton);
        document.body.removeChild(previewImage);
        setCameraLoading(false); // Hide loading modal on cleanup
      };

      captureButton.onclick = () => {
        // Create canvas to capture frame
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0);

        // Convert to base64
        capturedBase64 = canvas.toDataURL("image/jpeg", 0.9);
        
        // Hide video and show preview
        video.style.display = "none";
        previewImage.src = capturedBase64;
        previewImage.style.display = "block";
        
        // Hide capture button, show send and retake buttons
        captureButton.style.display = "none";
        sendButton.style.display = "block";
        retakeButton.style.display = "block";
      };

      retakeButton.onclick = () => {
        // Hide preview, show video again
        previewImage.style.display = "none";
        video.style.display = "block";
        
        // Show capture button, hide send and retake buttons
        captureButton.style.display = "block";
        sendButton.style.display = "none";
        retakeButton.style.display = "none";
        
        capturedBase64 = null;
      };

      sendButton.onclick = async () => {
        if (!capturedBase64) return;
        
        cleanup();
        resolve(capturedBase64);
      };

      closeButton.onclick = () => {
        cleanup();
        reject(new Error("Camera capture cancelled"));
      };
    });

    console.log("📸 Image captured, size:", imageBase64.length);
    return imageBase64;
  };

  // Send photo
  const sendPhoto = async (photoBase64) => {
    if (!photoBase64 || !customer) return;

    setSendingPhoto(true);

    // Convert base64 to array format expected by Cloud Function
    const photoBase64Array = [photoBase64];

    // Optimistic update - add message to Zustand store immediately
    const tempId = `temp_photo_${Date.now()}`;
    const optimisticMessage = {
      id: tempId,
      text: "",
      direction: 'outbound',
      sentAt: { seconds: Date.now() / 1000 },
      status: 'sending',
      photoURL: photoBase64, // Show captured photo immediately
    };

    const currentMessages = useMessagesStore.getState().getMessages(cleanPhoneNumber(customer.phone));
    useMessagesStore.getState().setConversation(customer.phone, {
      messages: [...currentMessages, optimisticMessage]
    });

    try {
      const sendPhotoMMS = httpsCallable(functions, 'sendPhotoMMS');
      const result = await sendPhotoMMS({
        customerPhone: customer.phone,
        customerName: customer.fullName,
        photoBase64Array: photoBase64Array,
        tenantId: tenantId,
        storeId: storeId,
        bookingId: customer.bookingId || null,
        category: 'messages',
        sentByUserId: getSelectedUserId(),
      });

      console.log('✅ Photo sent successfully:', result.data);

      // Update message with real data from server
      const messagesAfterOptimistic = useMessagesStore.getState().getMessages(cleanPhoneNumber(customer.phone));
      const updatedMessages = messagesAfterOptimistic.map(msg => 
        msg.id === tempId 
          ? {
              ...msg,
              id: result.data?.messageId || result.data?.messageSid || tempId,
              status: 'sent',
              photoURL: result.data?.photoUrls?.[0] || photoBase64,
            }
          : msg
      );

      useMessagesStore.getState().setConversation(customer.phone, {
        messages: updatedMessages
      });

      // Same as the text path: sendPhotoMMS opened replies server-side, so mirror
      // it here rather than leaving the switch stale.
      setCanRespond(true);

      // Scroll to bottom
      setTimeout(() => {
        if (flatListRef.current) {
          flatListRef.current.scrollToEnd({ animated: true });
        }
      }, 100);

    } catch (error) {
      console.error('❌ Failed to send photo:', error);
      
      // Remove optimistic message from store on error
      const currentMessages = useMessagesStore.getState().getMessages(cleanPhoneNumber(customer.phone));
      const filteredMessages = currentMessages.filter(msg => msg.id !== tempId);
      useMessagesStore.getState().setConversation(customer.phone, {
        messages: filteredMessages
      });

      alert('Failed to send photo: ' + (error.message || 'Please try again.'));
    } finally {
      setSendingPhoto(false);
    }
  };

  const handleCameraPress = async () => {
    if (!customer) return;
    
    try {
      const photoBase64 = await capturePhoto();
      await sendPhoto(photoBase64);
    } catch (error) {
      if (error.message && !error.message.includes("cancelled")) {
        console.error("❌ Error capturing photo:", error);
        alert("Failed to capture photo: " + error.message);
      }
    }
  };

  const handleEmailPress = () => {
    if (customer.email) {
      navigate('/gmail-inbox', {
        state: {
          customerEmail: customer.email,
          customerName: customer.fullName,
          composeTo: customer.email
        }
      });
    }
  };
  
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp?.seconds ? new Date(timestamp.seconds * 1000) : null;
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    // Less than 1 minute
    if (diffMins < 1) return 'Just now';
    
    // Less than 1 hour
    if (diffMins < 60) return `${diffMins}m ago`;
    
    // Less than 24 hours
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    // Show date
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Render message text with clickable links
  /**
   * Delivery state for an outbound bubble.
   *
   * twilioStatusCallback writes two fields: `status` (ours, which collapses
   * undelivered into "failed") and `deliveryStatus` (Twilio's raw word). Read
   * the raw one first -- "undelivered" means a carrier refused the message,
   * "failed" means it never left Twilio, and whoever has to chase it needs to
   * know which. `errorCode` is shown alongside because it is the only thing that
   * distinguishes, say, a bad number from carrier spam filtering (30007).
   */
  const deliveryLabel = (message) => {
    const raw = message.deliveryStatus || message.status;
    switch (raw) {
      case 'delivered': return { text: '\u2713 Delivered', bad: false };
      case 'undelivered': return { text: '\u26a0 Undelivered', bad: true };
      case 'failed': return { text: '\u26a0 Failed', bad: true };
      case 'sent': return { text: 'Sent', bad: false };
      case 'queued':
      case 'sending': return { text: 'Sending\u2026', bad: false };
      default: return raw ? { text: String(raw), bad: false } : null;
    }
  };

  const renderMessageText = (text, isOutbound) => {
    if (!text) return null;

    // Regex to match URLs (http, https, or www)
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
    const parts = text.split(urlRegex);
    
    return (
      <Text style={[
        styles.messageText,
        isOutbound ? styles.messageTextOutbound : styles.messageTextInbound
      ]}>
        {parts.map((part, index) => {
          // Check if this part is a URL (create new regex for each check to avoid global flag issues)
          const urlCheckRegex = /^(https?:\/\/|www\.)/i;
          const isUrl = urlCheckRegex.test(part);
          if (isUrl) {
            // Ensure URL has protocol
            const url = part.startsWith('http') ? part : `https://${part}`;
            return (
              <Text
                key={index}
                style={[
                  styles.messageLinkText,
                  isOutbound ? styles.messageLinkTextOutbound : styles.messageLinkTextInbound
                ]}
                onPress={() => Linking.openURL(url)}
              >
                {part}
              </Text>
            );
          }
          return <Text key={index}>{part}</Text>;
        })}
      </Text>
    );
  };
  
  // Calculate effective canRespond state for display
  const isCheckboxChecked = canRespond;
  
  if (!customer) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>No customer selected</Text>
      </View>
    );
  }
  
  return (
    <KeyboardAvoidingView 
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      {/* Floating Back Button */}
      <TouchableOpacity dataSet={{ hover: "lift" }}
        style={[
          styles.floatingBackButton,
          isMobile === false && styles.floatingBackButtonDesktop
        ]}
        onPress={() => navigate(-1)}
      >
        <Text style={styles.floatingBackButtonText}>←</Text>
      </TouchableOpacity>

      <View style={styles.contentWrapper}>
        {/* Header - 20% height */}
        <View style={styles.headerContainer}>
          <Text style={styles.headerName}>{customer.fullName}</Text>
          <View style={styles.headerDetails}>
            <TouchableOpacity onPress={handlePhonePress} disabled={!isMobile}>
              <Text style={[styles.headerPhone, isMobile && styles.linkText]}>
                {formatPhoneNumber(customer.phone)}
              </Text>
            </TouchableOpacity>
            
            {/* Checkbox Section */}
            <View style={styles.checkboxWrapper}>
              <View style={styles.checkboxRow}>
                <Switch
                  value={isCheckboxChecked}
                  onValueChange={handleCanRespondToggle}
                  trackColor={{ false: "#8A9794", true: "#9FC5BE" }}
                  thumbColor={isCheckboxChecked ? "#0E7C6B" : "#EDF1F0"}
                  ios_backgroundColor="#0F1A19"
                />
                <Text style={styles.checkboxLabel}>Can Respond</Text>
              </View>
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={[
                  styles.autoFilterButton,
                  showAutomatedOnly && styles.autoFilterButtonActive
                ]}
                onPress={() => setShowAutomatedOnly((prev) => !prev)}
              >
                <Text
                  style={[
                    styles.autoFilterButtonText,
                    showAutomatedOnly && styles.autoFilterButtonTextActive
                  ]}
                >
                  Auto Only ({automatedMessages.length})
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>

        {/* Messages List - 70% height */}
        <View style={styles.messagesContainer}>
          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#0E7C6B" />
              <Text style={styles.loadingText}>Loading messages...</Text>
            </View>
          ) : displayedMessages.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyText}>
                {showAutomatedOnly ? 'No automated messages yet' : 'No messages yet'}
              </Text>
              <Text style={styles.emptySubtext}>
                {showAutomatedOnly
                  ? 'Turn off Auto Only to see all messages'
                  : 'Start a conversation by sending a message'}
              </Text>
            </View>
          ) : (
            <FlatList
              ref={flatListRef}
              data={displayedMessages}
              keyExtractor={(item) => item.id}
              renderItem={({ item }) => {
                const isOutbound = item.direction === 'outbound';
                // Inbound messages have no delivery state of ours to report.
                const delivery = isOutbound ? deliveryLabel(item) : null;
                
              return (
                <View style={[
                  styles.messageBubble,
                  isOutbound ? styles.messageBubbleOutbound : styles.messageBubbleInbound
                ]}>
                  {item.messageType === 'automated_booking_sms' && (
                    <View
                      style={[
                        styles.automatedTag,
                        isOutbound
                          ? styles.automatedTagOutbound
                          : styles.automatedTagInbound
                      ]}
                    >
                      <Text
                        style={[
                          styles.automatedTagText,
                          isOutbound
                            ? styles.automatedTagTextOutbound
                            : styles.automatedTagTextInbound
                        ]}
                      >
                        AUTO
                      </Text>
                    </View>
                  )}
                  {item.photoURL && (
                    <View style={styles.attachmentsContainer}>
                      <TouchableOpacity 
                        onPress={() => {
                          setPhotoModalUrl(item.photoURL);
                          setIsPhotoModalOpen(true);
                        }}
                        style={styles.photoThumbnailContainer}
                      >
                        <Image 
                          source={{ uri: item.photoURL }} 
                          style={styles.attachmentImage} 
                          resizeMode="cover"
                          onError={(error) => {
                            console.error('Error loading image:', error, 'URL:', item.photoURL);
                          }}
                          onLoad={() => {
                            console.log('Image loaded successfully:', item.photoURL);
                          }}
                        />
                      </TouchableOpacity>
                    </View>
                  )}
                  {item.videoURL && (
                    <View style={styles.videoLinkContainer}>
                      <TouchableOpacity dataSet={{ hover: "lift" }} 
                        onPress={() => {
                          setVideoModalUrl(item.videoURL);
                          setIsVideoModalOpen(true);
                        }}
                        style={[
                          styles.videoLinkButton,
                          !isOutbound && styles.videoLinkButtonInbound
                        ]}
                      >
                        <Text style={[
                          styles.videoLinkText,
                          !isOutbound && styles.videoLinkTextInbound
                        ]}>📹 Video here</Text>
                      </TouchableOpacity>
                    </View>
                  )}
                  {renderMessageText(item.text, isOutbound)}
                  <Text style={[
                    styles.messageTime,
                    isOutbound ? styles.messageTimeOutbound : styles.messageTimeInbound
                  ]}>
                    {formatTime(item.sentAt || item.receivedAt)}
                  </Text>
                  {/* Live: the conversation listener is an onSnapshot, so a status
                      written by twilioStatusCallback repaints this with no refresh. */}
                  {delivery && (
                    <Text style={[styles.deliveryStatus, delivery.bad && styles.deliveryStatusBad]}>
                      {delivery.text}
                      {delivery.bad && item.errorCode ? ` (${item.errorCode})` : ""}
                    </Text>
                  )}
                </View>
              );
              }}
              contentContainerStyle={styles.messagesList}
              onContentSizeChange={() => {
                // Scroll to bottom when content size changes (e.g., new messages loaded)
                if (flatListRef.current) {
                  setTimeout(() => {
                    if (flatListRef.current) {
                      flatListRef.current.scrollToEnd({ animated: false });
                    }
                  }, 50);
                }
              }}
              onLayout={() => {
                // Scroll to bottom when FlatList layout is calculated
                if (flatListRef.current && displayedMessages.length > 0) {
                  setTimeout(() => {
                    if (flatListRef.current) {
                      flatListRef.current.scrollToEnd({ animated: false });
                    }
                  }, 100);
                }
              }}
            />
          )}
        </View>

        {/* Camera Loading Modal */}
        {cameraLoading && (
          <View style={styles.cameraLoadingModal}>
            <View style={styles.cameraLoadingContainer}>
              <ActivityIndicator size="large" color="#FFFFFF" />
              <Text style={styles.cameraLoadingText}>Loading camera...</Text>
            </View>
          </View>
        )}

        {/* Video Modal Overlay */}
        {isVideoModalOpen && videoModalUrl && (
          <View style={styles.videoModalOverlay}>
            <View style={styles.videoModalContainer}>
              {/* Close Button */}
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={styles.videoModalCloseButton}
                onPress={() => {
                  if (videoPlayerRef.current) {
                    videoPlayerRef.current.pause();
                  }
                  setIsVideoModalOpen(false);
                  setVideoModalUrl(null);
                  setIsVideoPlaying(false);
                }}
              >
                <Text style={styles.videoModalCloseText}>✕</Text>
              </TouchableOpacity>

              {/* Video Player */}
              <video
                ref={videoPlayerRef}
                src={videoModalUrl}
                style={styles.videoModalPlayer}
                controls={false}
                playsInline
                onPlay={() => setIsVideoPlaying(true)}
                onPause={() => setIsVideoPlaying(false)}
                onEnded={() => setIsVideoPlaying(false)}
              />

              {/* Video Controls */}
              <View style={styles.videoModalControls}>
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={styles.videoControlButton}
                  onPress={() => {
                    if (videoPlayerRef.current) {
                      if (isVideoPlaying) {
                        videoPlayerRef.current.pause();
                      } else {
                        videoPlayerRef.current.play();
                      }
                    }
                  }}
                >
                  <Text style={styles.videoControlButtonText}>
                    {isVideoPlaying ? '⏸ Pause' : '▶ Play'}
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={styles.videoControlButton}
                  onPress={() => {
                    if (videoPlayerRef.current) {
                      videoPlayerRef.current.currentTime = 0;
                      videoPlayerRef.current.play();
                    }
                  }}
                >
                  <Text style={styles.videoControlButtonText}>⏮ Restart</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        )}

        {/* Photo Modal Overlay */}
        {isPhotoModalOpen && photoModalUrl && (
          <View style={styles.photoModalOverlay}>
            <View style={styles.photoModalContainer}>
              {/* Close Button */}
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={styles.photoModalCloseButton}
                onPress={() => {
                  setIsPhotoModalOpen(false);
                  setPhotoModalUrl(null);
                }}
              >
                <Text style={styles.photoModalCloseText}>✕</Text>
              </TouchableOpacity>

              {/* Photo Image */}
              <View style={styles.photoModalImageContainer}>
                {Platform.OS === 'web' ? (
                  <img 
                    src={photoModalUrl}
                    alt="Photo"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                    }}
                    onError={(error) => {
                      console.error('Error loading photo in modal (web):', error, 'URL:', photoModalUrl);
                    }}
                    onLoad={() => {
                      console.log('Photo loaded in modal (web):', photoModalUrl);
                    }}
                  />
                ) : (
                  <Image 
                    source={{ uri: photoModalUrl }} 
                    style={styles.photoModalImage} 
                    resizeMode="contain"
                    onError={(error) => {
                      console.error('Error loading photo in modal:', error, 'URL:', photoModalUrl);
                    }}
                    onLoad={(event) => {
                      const { width, height } = event.nativeEvent.source;
                      console.log('Photo loaded in modal:', { width, height, url: photoModalUrl });
                    }}
                  />
                )}
              </View>
            </View>
          </View>
        )}
        
        <View style={styles.inputContainer}>
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[styles.cameraButton, !customer && styles.cameraButtonDisabled]}
            onPress={handleCameraPress}
            disabled={!customer}
          >
            <Text style={styles.cameraButtonIcon}>📸</Text>
          </TouchableOpacity>
          <TextInput
            style={[styles.input, { height: Math.min(Math.max(40, inputHeight), 100) }]}
            placeholder="Type a message..."
            placeholderTextColor="#AEB8B6"
            value={messageText}
            onChangeText={setMessageText}
            onContentSizeChange={(event) => {
              setInputHeight(event.nativeEvent.contentSize.height);
            }}
            multiline
            maxLength={1600} // SMS limit
            editable={!sending}
          />
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[styles.sendButton, (!messageText.trim() || sending) && styles.sendButtonDisabled]}
            onPress={handleSendMessage}
            disabled={!messageText.trim() || sending}
          >
            {sending ? (
              <ActivityIndicator color="#FFFFFF" size="small" />
            ) : (
              <Text style={styles.sendButtonIcon}>➤</Text>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100vh',
    backgroundColor: '#F6F8F7',
  },
  contentWrapper: {
    flex: 1,
    width: '100%',
    maxWidth: 800,
    alignSelf: 'center',
  },
  headerContainer: {
    height: '20vh',
    backgroundColor: '#F6F8F7',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
  },
  headerName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#2E3A38',
    marginBottom: 8,
  },
  headerDetails: {
    alignItems: 'center',
    gap: 8,
  },
  headerPhone: {
    fontSize: 16,
    color: '#5C6B68',
  },
  linkText: {
    color: '#0E7C6B',
    textDecorationLine: 'underline',
  },
  messageLinkText: {
    textDecorationLine: 'underline',
  },
  messageLinkTextOutbound: {
    color: '#FFFFFF',
  },
  messageLinkTextInbound: {
    color: '#0E7C6B',
  },
  checkboxWrapper: {
    alignItems: 'center',
    marginVertical: 4,
    gap: 8,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  checkboxLabel: {
    fontSize: 14,
    color: '#2E3A38',
    fontWeight: '500',
  },
  autoFilterButton: {
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#0E7C6B',
    backgroundColor: '#FFFFFF',
  },
  autoFilterButtonActive: {
    backgroundColor: '#0E7C6B',
  },
  autoFilterButtonText: {
    fontSize: 12,
    color: '#0E7C6B',
    fontWeight: '600',
  },
  autoFilterButtonTextActive: {
    color: '#FFFFFF',
  },
  messagesContainer: {
    height: '65vh',
    backgroundColor: '#FFFFFF',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 15,
    fontSize: 16,
    color: '#5C6B68',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#5C6B68',
    marginBottom: 8,
    textAlign: 'center',
  },
  emptySubtext: {
    fontSize: 14,
    color: '#8A9794',
    textAlign: 'center',
  },
  messagesList: {
    paddingHorizontal: 15,
    paddingVertical: 10,
  },
  messageBubble: {
    maxWidth: '75%',
    padding: 12,
    borderRadius: 12,
    marginVertical: 4,
  },
  automatedTag: {
    alignSelf: 'flex-start',
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 3,
    marginBottom: 6,
  },
  automatedTagOutbound: {
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
  },
  automatedTagInbound: {
    backgroundColor: '#DDE3E2',
  },
  automatedTagText: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.4,
  },
  automatedTagTextOutbound: {
    color: '#FFFFFF',
  },
  automatedTagTextInbound: {
    color: '#2E3A38',
  },
  messageBubbleOutbound: {
    alignSelf: 'flex-end',
    backgroundColor: '#0E7C6B',
    borderBottomRightRadius: 4,
  },
  messageBubbleInbound: {
    alignSelf: 'flex-start',
    backgroundColor: '#DDE3E2',
    borderBottomLeftRadius: 4,
  },
  messageText: {
    fontSize: 16,
    lineHeight: 20,
  },
  messageTextOutbound: {
    color: '#FFFFFF',
  },
  messageTextInbound: {
    color: '#0F1A19',
  },
  attachmentsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 8,
  },
  photoThumbnailContainer: {
    borderRadius: 6,
    overflow: 'hidden',
  },
  attachmentImage: {
    width: 200,
    height: 150,
    borderRadius: 6,
    backgroundColor: '#EDF1F0',
    minHeight: 150,
  },
  videoLinkContainer: {
    marginBottom: 8,
  },
  videoLinkButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  videoLinkButtonInbound: {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    borderColor: 'rgba(0, 0, 0, 0.1)',
  },
  videoLinkText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  videoLinkTextInbound: {
    color: '#0E7C6B',
  },
  videoModalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    zIndex: 1000,
    justifyContent: 'center',
    alignItems: 'center',
  },
  videoModalContainer: {
    width: '90%',
    maxWidth: 800,
    backgroundColor: '#0F1A19',
    borderRadius: 12,
    overflow: 'hidden',
    position: 'relative',
  },
  videoModalCloseButton: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1001,
  },
  videoModalCloseText: {
    color: '#FFFFFF',
    fontSize: 24,
    fontWeight: 'bold',
  },
  videoModalPlayer: {
    width: '100%',
    height: 'auto',
    minHeight: 300,
    backgroundColor: '#0F1A19',
  },
  videoModalControls: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 15,
    padding: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
  },
  videoControlButton: {
    backgroundColor: '#0E7C6B',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 6,
    minWidth: 100,
    alignItems: 'center',
  },
  videoControlButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  photoModalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    zIndex: 1000,
    justifyContent: 'center',
    alignItems: 'center',
  },
  photoModalContainer: {
    width: '90%',
    maxWidth: 800,
    backgroundColor: '#0F1A19',
    borderRadius: 12,
    overflow: 'hidden',
    position: 'relative',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  photoModalCloseButton: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1001,
  },
  photoModalCloseText: {
    color: '#0F1A19',
    fontSize: 24,
    fontWeight: 'bold',
  },
  photoModalImageContainer: {
    width: '100%',
    height: '70vh',
    maxHeight: 600,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#0F1A19',
  },
  photoModalImage: {
    width: '100%',
    height: '100%',
    backgroundColor: '#0F1A19',
  },
  messageTime: {
    fontSize: 11,
    marginTop: 4,
  },
  messageTimeOutbound: {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  messageTimeInbound: {
    color: '#8A9794',
  },
  // Outbound bubbles are a solid colour with light text, so both of these are
  // tuned to sit on that rather than on the page background.
  deliveryStatus: {
    fontSize: 10,
    marginTop: 2,
    color: 'rgba(255, 255, 255, 0.75)',
  },
  deliveryStatusBad: {
    color: '#FFD2CE',
    fontWeight: '700',
  },
  inputContainer: {
    height: '15vh',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 15,
    borderTopWidth: 1,
    borderTopColor: '#DDE3E2',
    gap: 10,
  },
  input: {
    flex: 1,
    minHeight: 40,
    // maxHeight removed here as it is handled inline
    backgroundColor: '#EDF1F0',
    borderRadius: 14,
    paddingHorizontal: 15,
    paddingVertical: 10,
    fontSize: 16,
    outlineStyle: 'none',
  },
  sendButton: {
    backgroundColor: '#0E7C6B',
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sendButtonDisabled: {
    backgroundColor: '#AEB8B6',
  },
  sendButtonIcon: {
    color: '#FFFFFF',
    fontSize: 20,
    fontWeight: 'bold',
  },
  cameraButton: {
    backgroundColor: 'transparent',
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cameraButtonDisabled: {
    backgroundColor: 'transparent',
    opacity: 0.5,
  },
  cameraButtonIcon: {
    fontSize: 24,
  },
  errorText: {
    fontSize: 16,
    color: '#C4453D',
    textAlign: 'center',
  },
  floatingBackButton: {
    position: 'absolute',
    top: 20,
    left: 20,
    width: 50,
    height: 50,
    backgroundColor: 'rgba(0, 122, 255, 0.5)',
    borderRadius: 25,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 10,
  },
  floatingBackButtonDesktop: {
    left: 'calc(50% - 400px + 20px)',
  },
  floatingBackButtonText: {
    color: '#FFFFFF',
    fontSize: 28,
    fontWeight: 'bold',
    lineHeight: 28,
  },
  cameraLoadingModal: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    zIndex: 10002,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cameraLoadingContainer: {
    alignItems: 'center',
    gap: 16,
  },
  cameraLoadingText: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '600',
  },
});

export default Messages;
